import { Outlet, Navigate } from "react-router-dom";
import {
  IconShieldCheck,
  IconSparkles,
  IconChartHistogram,
  IconUsers,
} from "@tabler/icons-react";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { Logo } from "@/components/ui/logo";

const features = [
  {
    icon: <IconChartHistogram size={18} />,
    title: "Pipeline visual",
    body: "Kanban drag-and-drop, metas VGV/VGL e dashboards do seu dia.",
  },
  {
    icon: <IconUsers size={18} />,
    title: "Co-broking sem fricção",
    body: "Convide parceiros, divida comissão e mantenha privacidade do proprietário.",
  },
  {
    icon: <IconShieldCheck size={18} />,
    title: "Contratos & assinaturas",
    body: "PDFs anexos, ClickSign/ZapSign e alerta de vencimento automático.",
  },
];

export function AuthLayout() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  return (
    // zoom 0.85 alinha a densidade do login ao app (AppLayout/onboarding usam o mesmo);
    // height compensa o zoom pra preencher a viewport inteira.
    <div
      className="grid overflow-hidden lg:grid-cols-[1.05fr_1fr]"
      style={{ zoom: 0.85, height: "calc(100vh / 0.85)" }}
    >
      {/* HERO — só desktop */}
      <aside className="relative hidden overflow-hidden bg-sidebar-grad text-white lg:flex lg:flex-col">
        {/* Mesh gradient blobs */}
        <div aria-hidden className="absolute inset-0 bg-argon-mesh opacity-50" />
        <div
          aria-hidden
          className="absolute -left-32 top-20 h-96 w-96 rounded-full bg-brand-500/30 blur-3xl"
        />
        <div
          aria-hidden
          className="absolute -right-24 bottom-10 h-[28rem] w-[28rem] rounded-full bg-pink/20 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.06),transparent_60%)]"
        />

        <div className="relative z-10 flex h-full flex-col p-10 xl:p-14">
          <div className="flex items-center gap-3">
            <Logo invert />
            <span className="rounded-full border border-white/20 bg-white/5 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white/70 backdrop-blur">
              CRM
            </span>
          </div>

          <div className="mt-auto space-y-8">
            <div className="space-y-4">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white/80 backdrop-blur">
                <IconSparkles size={12} />
                Feito pra corretor brasileiro
              </span>
              <h1 className="text-[44px] font-bold leading-[1.05] tracking-tight xl:text-5xl">
                O CRM que entende{" "}
                <span className="bg-gradient-to-r from-pink-200 via-white to-brand-200 bg-clip-text text-transparent">
                  como você vende imóveis
                </span>
              </h1>
              <p className="max-w-md text-[15px] leading-relaxed text-white/70">
                Captação, pipeline, propostas, contratos, vistorias e
                comissão — num só lugar. Mock-vs-API por módulo pra você
                evoluir sem travar a operação.
              </p>
            </div>

            <ul className="space-y-3">
              {features.map((f) => (
                <li key={f.title} className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/10 text-white shadow-inner-light backdrop-blur">
                    {f.icon}
                  </span>
                  <div>
                    <p className="text-[13px] font-semibold text-white">{f.title}</p>
                    <p className="text-[12px] text-white/60">{f.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <footer className="relative z-10 mt-10 flex items-center justify-between text-[11px] text-white/50">
            <span>© ImobGo · Todos os direitos reservados</span>
            <span className="font-mono">v2.0</span>
          </footer>
        </div>
      </aside>

      {/* FORM */}
      <div className="relative flex items-center justify-center overflow-y-auto overflow-x-hidden bg-bg px-4 py-10 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-32 top-10 h-72 w-72 rounded-full bg-brand-400/15 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -left-20 bottom-10 h-80 w-80 rounded-full bg-info/10 blur-3xl"
        />

        <div className="relative w-full max-w-md">
          <div className="mb-8 flex items-center justify-center gap-3 lg:hidden">
            <Logo />
          </div>
          <Outlet />
        </div>
      </div>
    </div>
  );
}
